// Rikudo generator & validator (hexagonal Hamiltonian path)
const DIRS = [[1, 0], [1, -1], [0, -1], [-1, 0], [-1, 1], [0, 1]];

function shuffle(a) {
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const key = (c) => `${c.q},${c.r}`;

export function hexCells(R) {
  const cells = [];
  for (let q = -R; q <= R; q++) {
    for (let r = Math.max(-R, -q - R); r <= Math.min(R, -q + R); r++) {
      cells.push({ q, r });
    }
  }
  return cells;
}

function buildAdj(cells) {
  const set = new Set(cells.map(key));
  const adj = {};
  cells.forEach((c) => {
    adj[key(c)] = DIRS.map(([dq, dr]) => ({ q: c.q + dq, r: c.r + dr }))
      .map(key)
      .filter((k) => set.has(k));
  });
  return adj;
}

function hamiltonian(cells, adj) {
  const K = cells.length;
  const keys = cells.map(key);
  const starts = shuffle(keys.slice());
  for (const start of starts) {
    const visited = new Set([start]);
    const order = [start];
    let steps = 0;
    const cap = 500000;
    const rec = (cur) => {
      if (order.length === K) return true;
      if (++steps > cap) return false;
      for (const nb of shuffle(adj[cur].slice())) {
        if (!visited.has(nb)) {
          visited.add(nb);
          order.push(nb);
          if (rec(nb)) return true;
          visited.delete(nb);
          order.pop();
        }
      }
      return false;
    };
    if (rec(start)) return order;
  }
  return null;
}

export function makeRikudo(R, givenRatio) {
  const cells = hexCells(R);
  const adj = buildAdj(cells);
  const K = cells.length;
  const path = hamiltonian(cells, adj);
  const numByKey = {};
  path.forEach((k, i) => { numByKey[k] = i + 1; });

  const givenKeys = new Set([path[0], path[K - 1]]);
  const target = Math.max(2, Math.floor(K * givenRatio));
  for (const k of shuffle(path.slice())) {
    if (givenKeys.size >= target) break;
    givenKeys.add(k);
  }

  const cellsOut = cells.map((c) => ({
    q: c.q,
    r: c.r,
    key: key(c),
    number: numByKey[key(c)],
    given: givenKeys.has(key(c)),
  }));
  return { R, K, cells: cellsOut, adj };
}

export function pixel(c, size) {
  return {
    x: size * Math.sqrt(3) * (c.q + c.r / 2),
    y: size * 1.5 * c.r,
  };
}

export function rikudoConflicts(placed, puzzle) {
  const { cells, adj } = puzzle;
  const byNum = {};
  cells.forEach((c) => { const v = placed[c.key]; if (v) byNum[v] = c.key; });
  const bad = new Set();
  cells.forEach((c) => {
    const v = placed[c.key];
    if (!v) return;
    for (const d of [-1, 1]) {
      const nb = byNum[v + d];
      if (nb && !adj[c.key].includes(nb)) bad.add(c.key);
    }
  });
  return bad;
}

export function checkRikudoWin(placed, puzzle) {
  const { K, cells, adj } = puzzle;
  const byNum = {};
  for (const c of cells) {
    const v = placed[c.key];
    if (!v || byNum[v]) return false;
    byNum[v] = c.key;
  }
  for (let i = 1; i < K; i++) {
    if (!adj[byNum[i]].includes(byNum[i + 1])) return false;
  }
  return true;
}
